import { useState, useEffect } from 'react';
import { TopNavbar } from './components/common/TopNavbar';
import { Dashboard } from './components/tickets/Dashboard';
import { TicketList, type TicketTab, type TicketSort, type DateRange, type ResolvedSort } from './components/tickets/TicketList';
import { TicketDetail } from './components/tickets/TicketDetail';
import { CloseConfirmModal } from './components/common/CloseConfirmModal';
import { CannedAdminScreen } from './components/admin/CannedAdminScreen';
import { AnnouncementsScreen } from './components/admin/AnnouncementsScreen';
import { useAuth } from './hooks/useAuth';
import { useBridge } from './hooks/useBridge';
import { bridgeService } from './services/bridge';
import { MiniappErrorBoundary } from './components/common/MiniappErrorBoundary';
import { TicketsProvider } from './hooks/useTickets';
import type { Ticket } from './types';

type AppView = 'dashboard' | 'tickets' | 'ticketDetail' | 'cannedAdmin' | 'announcements';

export function App() {
  // Check URL params immediately so ticketId-deep-links land on TicketDetail without a dashboard flash.
  // Embedded-mode detection comes from useBridge() below (synchronous getter, same URL check).
  const params = new URLSearchParams(window.location.search);
  const initialTicketId = params.get('ticketId');

  const [currentView, setCurrentView] = useState<AppView>(
    initialTicketId ? 'ticketDetail' : 'dashboard'
  );
  const [ticketListTab, setTicketListTab] = useState<TicketTab>('new');
  /** Persists while navigating to ticket detail so returning restores the same list page. */
  const [listPages, setListPages] = useState<Record<TicketTab, number>>({
    new: 1,
    in_progress: 1,
    resolved: 1,
  });
  /** Global filter state — search query + date range apply across all tabs
   *  so switching tabs preserves the user's selections. Sort is per-tab
   *  because the Resolved tab's sort options (most_recent / most_tickets /
   *  name_az) are user-centric, while New/In-Progress sort tickets. Pages
   *  stay per-tab so each tab remembers its own paging position within the
   *  filtered set. */
  const [listSort, setListSort] = useState<TicketSort>('unread');
  const [resolvedSort, setResolvedSort] = useState<ResolvedSort>('most_recent');
  const [listDateRange, setListDateRange] = useState<DateRange>({});
  const [listQuery, setListQuery] = useState<string>('');
  /** Resolved-tab-only filters — these don't make sense for ticket-row tabs. */
  const [listCategories, setListCategories] = useState<string[]>([]);
  const [listHasFollowup, setListHasFollowup] = useState<boolean>(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  /** Row used to seed ticket detail header without a second "loading shell" when ids match. */
  const [detailListTicket, setDetailListTicket] = useState<Ticket | null>(null);
  /** Where ticket detail’s Back should land (tickets list keeps current tab via ticketListTab) */
  const [ticketDetailReturnTo, setTicketDetailReturnTo] = useState<'dashboard' | 'tickets'>('dashboard');

  const {
    isAuthenticated,
    isLoading: authLoading,
    user,
    isInSuperApp,
    deviceNotAuthorized,
    logout,
  } = useAuth();

  const { setAppBarVisible, isReady: bridgeReady, safeArea } = useBridge();

  // The SuperApp's Flutter InAppWebView frequently reports
  // env(safe-area-inset-*) as 0, which lets TopNavbar / the chat header slide
  // under the status bar. The host hands us the real insets via
  // bridge.safeArea (initData.viewPadding*), so mirror them into CSS vars that
  // .safe-area-top/.safe-area-bottom fall back to (see index.css). No-op when
  // env() already works.
  useEffect(() => {
    if (!bridgeReady) return;
    const root = document.documentElement;
    root.style.setProperty('--app-safe-area-top', `${safeArea.top}px`);
    root.style.setProperty('--app-safe-area-bottom', `${safeArea.bottom}px`);
  }, [bridgeReady, safeArea.top, safeArea.bottom]);

  // Phase 35 — JS confirmation modal for the X-close. Tap Yes →
  // revoke the JWT via useAuth().logout() then close the miniapp.
  // Replaces the native closeMiniAppWithExitDialog because the
  // native flow gives JS no Yes/No callback, so we can't run the
  // revocation hook from there.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Hide the SuperApp's native AppBar (it adds an unwanted back arrow).
  // Our TopNavbar handles title + close instead.
  useEffect(() => {
    if (bridgeReady && isInSuperApp) {
      setAppBarVisible(false);
    }
  }, [bridgeReady, isInSuperApp, setAppBarVisible]);

  const handleNavigateToTickets = (tab: TicketTab = 'new') => {
    setTicketListTab(tab);
    setListPages((p) => ({ ...p, [tab]: 1 }));
    setCurrentView('tickets');
  };

  const handleSelectTicket = (ticket: Ticket) => {
    setDetailListTicket(ticket);
    setSelectedTicketId(ticket.ticketId);
    setTicketDetailReturnTo(currentView === 'tickets' ? 'tickets' : 'dashboard');
    setCurrentView('ticketDetail');
  };

  const handleBackToDashboard = () => {
    setSelectedTicketId(null);
    setDetailListTicket(null);
    setCurrentView('dashboard');
  };

  const handleBackFromTicketDetail = () => {
    setSelectedTicketId(null);
    setDetailListTicket(null);
    setCurrentView(ticketDetailReturnTo === 'tickets' ? 'tickets' : 'dashboard');
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-secondary">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-text-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  if (deviceNotAuthorized) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-secondary">
        <div className="text-center px-6">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Device Not Authorized</h2>
          <p className="text-text-secondary text-sm">Contact your admin to provision access for this device.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (!isInSuperApp) {
      return (
        <div className="flex items-center justify-center h-screen bg-surface-secondary">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-text-primary mb-2">SuperApp Required</h2>
            <p className="text-text-secondary text-sm">Please open this app from the DG SuperApp.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-screen bg-surface-secondary">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-text-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Connecting...</p>
        </div>
      </div>
    );
  }

  const displayName = user?.username || 'Agent';
  // Phase 37 — frontend UX hide for the canned-responses admin entry. The chip
  // in TopNavbar is tappable ONLY for level>=2; the real enforcement is the
  // server-side requireLevel(2) gate (37-03). Default level 1 when unknown.
  const isAdmin = (user?.level ?? 1) >= 2;

  return (
    <TicketsProvider>
      <MiniappErrorBoundary>
      {currentView === 'ticketDetail' && selectedTicketId ? (
        <TicketDetail
          ticketId={selectedTicketId}
          listTicket={detailListTicket?.ticketId === selectedTicketId ? detailListTicket : null}
          onBack={handleBackFromTicketDetail}
          onSelectTicket={handleSelectTicket}
          onOpenAdmin={isAdmin ? () => setCurrentView('cannedAdmin') : undefined}
        />
      ) : (
        <div className="h-screen flex flex-col overflow-hidden bg-surface-secondary">
          <TopNavbar
            agentName={displayName}
            level={user?.level ?? 1}
            onBack={currentView === 'tickets' ? handleBackToDashboard : undefined}
            onClose={isInSuperApp ? () => setShowCloseConfirm(true) : undefined}
            onOpenAdmin={isAdmin ? () => setCurrentView('cannedAdmin') : undefined}
            onOpenAnnouncements={isAdmin ? () => setCurrentView('announcements') : undefined}
          />

          <div
            className={
              currentView === 'tickets' || currentView === 'announcements'
                ? 'flex-1 min-h-0 flex flex-col overflow-hidden'
                : 'flex-1 overflow-y-auto scroll-smooth'
            }
          >
            <div
              className={
                currentView === 'tickets' || currentView === 'announcements'
                  ? 'max-w-4xl mx-auto w-full flex-1 flex flex-col min-h-0'
                  : 'max-w-4xl mx-auto min-h-full'
              }
            >
              {currentView === 'dashboard' && (
                <Dashboard
                  onNavigateToTickets={handleNavigateToTickets}
                  onSelectTicket={handleSelectTicket}
                  onOpenAdmin={isAdmin ? () => setCurrentView('cannedAdmin') : undefined}
                />
              )}
              {currentView === 'tickets' && (
                <TicketList
                  initialTab={ticketListTab}
                  listPages={listPages}
                  onListPagesChange={setListPages}
                  listSort={listSort}
                  onListSortChange={setListSort}
                  resolvedSort={resolvedSort}
                  onResolvedSortChange={setResolvedSort}
                  listDateRange={listDateRange}
                  onListDateRangeChange={setListDateRange}
                  listQuery={listQuery}
                  onListQueryChange={setListQuery}
                  listCategories={listCategories}
                  onListCategoriesChange={setListCategories}
                  listHasFollowup={listHasFollowup}
                  onListHasFollowupChange={setListHasFollowup}
                  onTabChange={(tab) => {
                    setTicketListTab(tab);
                  }}
                  onSelectTicket={handleSelectTicket}
                />
              )}
              {currentView === 'cannedAdmin' && (
                <CannedAdminScreen onBack={() => setCurrentView('dashboard')} />
              )}
              {currentView === 'announcements' && (
                <AnnouncementsScreen onBack={() => setCurrentView('dashboard')} defaultTestChatId={user?.telegramUserId ?? ''} />
              )}
            </div>
          </div>
        </div>
      )}

      {showCloseConfirm && (
        <CloseConfirmModal
          onCancel={() => setShowCloseConfirm(false)}
          onConfirm={async () => {
            setShowCloseConfirm(false);
            // Revoke the JWT server-side + clear local state.
            // logout() swallows network failures, so this await
            // never throws — close still fires.
            await logout();
            await bridgeService.close();
          }}
        />
      )}
      </MiniappErrorBoundary>
    </TicketsProvider>
  );
}
